import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Building2, Users as UsersIcon, CreditCard, TrendingUp, Search,
  Plus, Download, Pause, Play, Trash2, KeyRound, Edit3, Copy, Activity, UserCheck, Palette,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PLAN_COLORS = {
  trial:      "bg-amber-100 text-amber-800 border-amber-200",
  starter:    "bg-sky-100 text-sky-800 border-sky-200",
  pro:        "bg-indigo-100 text-indigo-800 border-indigo-200",
  enterprise: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const STATUS_COLORS = {
  active:    "bg-emerald-100 text-emerald-800",
  trial:     "bg-amber-100 text-amber-800",
  suspended: "bg-slate-200 text-slate-700",
  expired:   "bg-rose-100 text-rose-700",
};

export default function MasterPanel() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);   // tenant being edited
  const [brandOpen, setBrandOpen] = useState(null); // tenant being branded
  const [deleteConfirm, setDeleteConfirm] = useState(null); // tenant being deleted
  const [arch, setArch] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/tenants");
      setTenants(data);
    } catch { setTenants([]); }
    finally { setLoading(false); }
  };
  const loadArch = async () => {
    try { const { data } = await api.get("/admin/architecture-status"); setArch(data); }
    catch { setArch(null); }
  };
  useEffect(() => { load(); loadArch(); }, []);

  if (!user?.is_platform_owner) {
    return <div className="p-10 text-center text-sm text-slate-500">Master Panel is restricted to the Platform Owner only. Client super admins do not have access.</div>;
  }

  const filtered = tenants.filter(t =>
    !q || t.name?.toLowerCase().includes(q.toLowerCase()) || t.slug?.toLowerCase().includes(q.toLowerCase())
  );
  const activeCount = tenants.filter(t => t.status === "active").length;
  const trialCount  = tenants.filter(t => t.status === "trial").length;
  const totalUsers  = tenants.reduce((a, t) => a + (t.users_count || 0), 0);

  const copyLicense = (key) => {
    navigator.clipboard?.writeText(key);
    toast.success("License key copied");
  };

  const suspend = async (t) => {
    try { await api.post(`/master/tenants/${t.id}/suspend`); toast.success("Tenant suspended"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reactivate = async (t) => {
    try { await api.post(`/master/tenants/${t.id}/reactivate`); toast.success("Tenant reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const regenLicense = async (t) => {
    try { const { data } = await api.post(`/master/tenants/${t.id}/regenerate-license`); toast.success("New license key issued"); copyLicense(data.license_key); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const impersonate = async (t) => {
    try {
      const { data } = await api.post(`/master/tenants/${t.id}/impersonate`);
      localStorage.setItem("hreye_token", data.token);
      const imp = data.impersonating_as || {};
      localStorage.setItem("hreye_user", JSON.stringify({
        id: imp.id || t.id,
        email: imp.email,
        role: imp.role,
        full_name: imp.full_name || imp.email,
        tenant_id: t.id,
        impersonating: true,
        original_user_id: user?.id,
        original_email: user?.email,
      }));
      toast.success(`Now impersonating ${t.name}`);
      window.location.href = "/";
    } catch (e) { toast.error(e?.response?.data?.detail || "Impersonation failed"); }
  };
  const doDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const { data } = await api.delete(`/master/tenants/${deleteConfirm.id}`);
      const n = Object.values(data.records_removed || {}).reduce((a,b)=>a+b,0);
      toast.success(`Tenant deleted · ${n} records removed`);
      setDeleteConfirm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const exportCsv = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("hreye_token");
      const res = await fetch(`${API_URL}/api/master/tenants-export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "tenants.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("Exported tenants.csv");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="master-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-600">Platform</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Master Panel</h1>
          <p className="text-sm text-slate-600 mt-1">Tenants · Licenses · Plans · Billing periods · Architecture status</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportCsv} variant="outline" data-testid="mp-export-csv"><Download className="w-4 h-4 mr-1.5"/>Export CSV</Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="mp-create-tenant"><Plus className="w-4 h-4 mr-1.5"/>New Tenant</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <StatBox label="Tenants" value={tenants.length} icon={Building2} tone="slate" />
        <StatBox label="Active" value={activeCount} icon={TrendingUp} tone="emerald" />
        <StatBox label="Trials" value={trialCount} icon={CreditCard} tone="amber" />
        <StatBox label="Total users" value={totalUsers} icon={UsersIcon} tone="cyan" />
      </div>

      <Card className="p-3 border-slate-200 shadow-none">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input placeholder="Search by name or slug…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="mp-search"/>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Seat limit</th>
                <th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3">License</th>
                <th className="px-4 py-3">Trial ends</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No tenants match</td></tr>}
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${t.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{t.slug}</div>
                  </td>
                  <td className="px-4 py-3"><Badge className={`${PLAN_COLORS[t.plan] || PLAN_COLORS.starter} border capitalize`}>{t.plan}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[t.status] || STATUS_COLORS.active} border-0 capitalize`}>{t.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{t.users_count ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{t.seat_limit}</td>
                  <td className="px-4 py-3 text-slate-700 capitalize">{t.billing_period}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <code className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 font-mono text-slate-700 truncate max-w-[140px] inline-block">{t.license_key}</code>
                      <button onClick={() => copyLicense(t.license_key)} className="text-slate-400 hover:text-slate-700" title="Copy"><Copy className="w-3 h-3"/></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-slate-500">{t.trial_ends_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-0.5">
                      <IconBtn title="Impersonate" onClick={() => impersonate(t)} testid={`tenant-impersonate-${t.slug}`}><UserCheck className="w-3.5 h-3.5"/></IconBtn>
                      <IconBtn title="Brand" onClick={() => setBrandOpen(t)} testid={`tenant-brand-${t.slug}`}><Palette className="w-3.5 h-3.5"/></IconBtn>
                      <IconBtn title="Edit" onClick={() => setEditOpen(t)} testid={`tenant-edit-${t.slug}`}><Edit3 className="w-3.5 h-3.5"/></IconBtn>
                      <IconBtn title="Regenerate license" onClick={() => regenLicense(t)} testid={`tenant-relicense-${t.slug}`}><KeyRound className="w-3.5 h-3.5"/></IconBtn>
                      {t.status !== "suspended"
                        ? <IconBtn title="Suspend" onClick={() => suspend(t)} testid={`tenant-suspend-${t.slug}`}><Pause className="w-3.5 h-3.5"/></IconBtn>
                        : <IconBtn title="Reactivate" onClick={() => reactivate(t)} testid={`tenant-reactivate-${t.slug}`}><Play className="w-3.5 h-3.5"/></IconBtn>}
                      <IconBtn title="Delete" onClick={() => setDeleteConfirm(t)} testid={`tenant-delete-${t.slug}`} danger><Trash2 className="w-3.5 h-3.5"/></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Architecture status */}
      {arch && (
        <Card className="p-5 border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-50/50 shadow-none">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center"><Activity className="w-4 h-4 text-cyan-700"/></div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-cyan-700">Round {arch.round}</div>
              <div className="text-base font-[Outfit] font-semibold text-slate-900">{arch.title} — <span className={arch.status === "foundation_complete" ? "text-emerald-700" : "text-amber-700"}>{arch.status.replace(/_/g, " ")}</span></div>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
            <div><div className="text-slate-500 uppercase tracking-wider font-semibold">Tenant-scoped collections</div><div className="mt-0.5 text-lg font-semibold text-slate-900">{arch.tenant_scoped_collections}</div></div>
            <div><div className="text-slate-500 uppercase tracking-wider font-semibold">JWT tenant-aware</div><div className="mt-0.5 text-lg font-semibold text-emerald-700">{arch.jwt_tenant_aware ? "Yes" : "No"}</div></div>
            <div><div className="text-slate-500 uppercase tracking-wider font-semibold">Default tenant seeded</div><div className="mt-0.5 text-lg font-semibold text-emerald-700">{arch.default_tenant_exists ? "Yes" : "No"}</div></div>
          </div>
          {arch.blockers_remaining?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Pending to go live</div>
              <ul className="text-xs text-slate-700 space-y-1">
                {arch.blockers_remaining.map((b, i) => <li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"/>{b}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}

      <TenantFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        onSaved={() => { setCreateOpen(false); load(); }}
      />
      <TenantFormDialog
        open={!!editOpen}
        onOpenChange={(v) => !v && setEditOpen(null)}
        mode="edit"
        tenant={editOpen}
        onSaved={() => { setEditOpen(null); load(); }}
      />

      <BrandDialog
        open={!!brandOpen}
        onOpenChange={(v) => !v && setBrandOpen(null)}
        tenant={brandOpen}
        onSaved={() => { setBrandOpen(null); load(); }}
      />

      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="mp-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Delete tenant?</DialogTitle>
            <DialogDescription>
              This will permanently remove <strong>{deleteConfirm?.name}</strong> and <strong>all</strong> their data across every module (users, tasks, messages, documents, attendance, etc). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="mp-delete-confirm">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, tone }) {
  const tones = {
    slate:   { bg: "bg-slate-100 text-slate-600",   text: "text-slate-900" },
    emerald: { bg: "bg-emerald-100 text-emerald-600", text: "text-emerald-800" },
    amber:   { bg: "bg-amber-100 text-amber-600",   text: "text-amber-800" },
    cyan:    { bg: "bg-cyan-100 text-cyan-600",     text: "text-cyan-800" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <Card className="p-4 border-slate-200 shadow-none">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${t.bg}`}><Icon className="w-3.5 h-3.5"/></div>
      </div>
      <div className={`mt-2 text-3xl font-[Outfit] font-semibold ${t.text}`}>{value}</div>
    </Card>
  );
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-7 h-7 flex items-center justify-center rounded hover:bg-white transition ${danger ? "text-rose-600 hover:text-rose-700" : "text-slate-500 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}

function TenantFormDialog({ open, onOpenChange, mode, tenant, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: "", slug: "", owner_email: "", plan: "trial",
    trial_days: 14, seat_limit: 50, billing_period: "monthly",
    status: "active", auto_renew: true,
  });

  useEffect(() => {
    if (isEdit && tenant) {
      setForm({
        name: tenant.name || "", slug: tenant.slug || "",
        owner_email: tenant.owner_email || "",
        plan: tenant.plan, status: tenant.status,
        seat_limit: tenant.seat_limit, billing_period: tenant.billing_period,
        auto_renew: tenant.auto_renew !== false, trial_days: 14,
      });
    } else if (!isEdit && open) {
      setForm({ name: "", slug: "", owner_email: "", plan: "trial", trial_days: 14, seat_limit: 50, billing_period: "monthly", status: "active", auto_renew: true });
    }
  }, [tenant, isEdit, open]);

  const save = async () => {
    try {
      if (isEdit) {
        await api.patch(`/master/tenants/${tenant.id}`, {
          name: form.name, plan: form.plan, status: form.status,
          billing_period: form.billing_period, seat_limit: Number(form.seat_limit),
          auto_renew: form.auto_renew,
        });
        toast.success("Tenant updated");
      } else {
        await api.post("/master/tenants", {
          name: form.name, slug: form.slug, owner_email: form.owner_email,
          plan: form.plan, trial_days: Number(form.trial_days),
          seat_limit: Number(form.seat_limit), billing_period: form.billing_period,
        });
        toast.success("Tenant created");
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={isEdit ? "mp-edit-dialog" : "mp-create-dialog"}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit tenant" : "Onboard a new tenant"}</DialogTitle>
          <DialogDescription>{isEdit ? "Update plan, status, seat limit and billing." : "Create a new workspace with its own isolated data."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Organization name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="tenant-name"/></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")})} disabled={isEdit} className="mt-1 font-mono" data-testid="tenant-slug"/></div>
          </div>
          {!isEdit && (
            <div><Label>Owner email</Label><Input type="email" value={form.owner_email} onChange={(e) => setForm({...form, owner_email: e.target.value})} className="mt-1" data-testid="tenant-owner-email"/></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plan</Label>
              <select value={form.plan} onChange={(e) => setForm({...form, plan: e.target.value})} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white text-sm px-2" data-testid="tenant-plan">
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <Label>Billing period</Label>
              <select value={form.billing_period} onChange={(e) => setForm({...form, billing_period: e.target.value})} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white text-sm px-2" data-testid="tenant-billing">
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Seat limit</Label><Input type="number" min="1" value={form.seat_limit} onChange={(e) => setForm({...form, seat_limit: e.target.value})} className="mt-1" data-testid="tenant-seats"/></div>
            {!isEdit && form.plan === "trial" && (
              <div><Label>Trial days</Label><Input type="number" min="1" max="90" value={form.trial_days} onChange={(e) => setForm({...form, trial_days: e.target.value})} className="mt-1" data-testid="tenant-trial-days"/></div>
            )}
            {isEdit && (
              <div>
                <Label>Status</Label>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white text-sm px-2" data-testid="tenant-status">
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="tenant-save">{isEdit ? "Save changes" : "Create tenant"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== BRAND DIALOG (live preview) ==================== */
function BrandDialog({ open, onOpenChange, tenant, onSaved }) {
  const [form, setForm] = useState({
    software_name: "", tagline: "", logo_initial: "",
    primary_color: "#0F172A", accent_color: "#06B6D4",
    login_gradient_preset: "midnight",
    login_gradient_from: "#0f172a", login_gradient_via: "#1e3a8a", login_gradient_to: "#4c1d95",
  });
  useEffect(() => {
    if (!tenant) return;
    const b = tenant.branding || {};
    setForm({
      software_name: b.software_name || tenant.name || "",
      tagline: b.tagline || "Your Smart HR Monitoring System",
      logo_initial: b.logo_initial || (tenant.name?.[0] || "H").toUpperCase(),
      primary_color: b.primary_color || "#0F172A",
      accent_color: b.accent_color || "#06B6D4",
      login_gradient_preset: b.login_gradient_preset || "midnight",
      login_gradient_from: b.login_gradient_from || "#0f172a",
      login_gradient_via: b.login_gradient_via || "#1e3a8a",
      login_gradient_to: b.login_gradient_to || "#4c1d95",
    });
  }, [tenant]);

  const PRESETS = {
    midnight: { from: "#0f172a", via: "#1e3a8a", to: "#4c1d95" },
    ocean:    { from: "#0c4a6e", via: "#0e7490", to: "#164e63" },
    aurora:   { from: "#111827", via: "#065f46", to: "#134e4a" },
    sunset:   { from: "#450a0a", via: "#7c2d12", to: "#881337" },
    forest:   { from: "#0f172a", via: "#064e3b", to: "#052e16" },
    royal:    { from: "#1e1b4b", via: "#4c1d95", to: "#581c87" },
  };

  const save = async () => {
    try {
      const branding = {
        software_name: form.software_name, tagline: form.tagline, logo_initial: form.logo_initial,
        primary_color: form.primary_color, accent_color: form.accent_color,
        login_gradient_preset: form.login_gradient_preset,
        login_gradient_from: form.login_gradient_from, login_gradient_via: form.login_gradient_via, login_gradient_to: form.login_gradient_to,
      };
      await api.patch(`/master/tenants/${tenant.id}/branding`, { branding });
      toast.success("Tenant branding saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const gStyle = {
    backgroundImage: `linear-gradient(135deg, ${form.login_gradient_from}, ${form.login_gradient_via}, ${form.login_gradient_to})`,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="brand-dialog">
        <DialogHeader>
          <DialogTitle>Tenant branding — {tenant?.name}</DialogTitle>
          <DialogDescription>Override the login page look for this tenant. Live preview on the right.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-1 max-h-[70vh] overflow-y-auto pr-1">
          {/* ========== FORM ========== */}
          <div className="space-y-3">
            <div><Label>Software name</Label><Input value={form.software_name} onChange={(e) => setForm({...form, software_name: e.target.value})} className="mt-1" data-testid="brand-name"/></div>
            <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({...form, tagline: e.target.value})} className="mt-1" data-testid="brand-tagline"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Logo initial</Label><Input maxLength={2} value={form.logo_initial} onChange={(e) => setForm({...form, logo_initial: e.target.value.toUpperCase()})} className="mt-1 font-semibold" data-testid="brand-initial"/></div>
              <div>
                <Label>Gradient preset</Label>
                <select value={form.login_gradient_preset} onChange={(e) => { const p = e.target.value; if (PRESETS[p]) setForm({...form, login_gradient_preset: p, login_gradient_from: PRESETS[p].from, login_gradient_via: PRESETS[p].via, login_gradient_to: PRESETS[p].to}); else setForm({...form, login_gradient_preset: p}); }} className="mt-1 w-full h-10 px-2 rounded-md border border-slate-200 bg-white text-sm" data-testid="brand-preset">
                  <option value="midnight">Midnight</option><option value="ocean">Ocean</option><option value="aurora">Aurora</option><option value="sunset">Sunset</option><option value="forest">Forest</option><option value="royal">Royal</option><option value="custom">Custom</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">From</Label><input type="color" value={form.login_gradient_from} onChange={(e) => setForm({...form, login_gradient_from: e.target.value, login_gradient_preset: "custom"})} className="mt-1 w-full h-10 rounded border border-slate-200 cursor-pointer"/></div>
              <div><Label className="text-xs">Via</Label><input type="color" value={form.login_gradient_via} onChange={(e) => setForm({...form, login_gradient_via: e.target.value, login_gradient_preset: "custom"})} className="mt-1 w-full h-10 rounded border border-slate-200 cursor-pointer"/></div>
              <div><Label className="text-xs">To</Label><input type="color" value={form.login_gradient_to} onChange={(e) => setForm({...form, login_gradient_to: e.target.value, login_gradient_preset: "custom"})} className="mt-1 w-full h-10 rounded border border-slate-200 cursor-pointer"/></div>
            </div>
          </div>

          {/* ========== LIVE PREVIEW ========== */}
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-900 relative h-[420px]" style={gStyle} data-testid="brand-preview">
            <div className="absolute inset-0 bg-black/50" />
            <div className="relative h-full flex items-center justify-center p-6">
              <div className="w-full max-w-xs bg-white rounded-2xl shadow-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 text-white flex items-center justify-center mx-auto text-lg font-bold" style={{ background: form.primary_color }}>{form.logo_initial || "H"}</div>
                <div className="mt-3 text-lg font-[Outfit] font-semibold text-slate-900">{form.software_name || "QrewHR"}</div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">{form.tagline}</div>
                <div className="mt-5 h-9 bg-slate-100 rounded-md" />
                <div className="mt-2 h-9 bg-slate-100 rounded-md" />
                <div className="mt-3 h-10 rounded-md font-semibold text-white flex items-center justify-center text-sm" style={{ background: form.accent_color }}>Sign in</div>
              </div>
            </div>
            <div className="absolute bottom-3 left-3 text-[10px] text-white/70 font-medium">Live preview · updates in real time</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={save} data-testid="brand-save">Save branding</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
